const _ = require("lodash");
const CR = require("./util/Cursor");
const LoadState = require("./util/LoadState");
const GradeChapter = require("../../../shared/GradeChapter");
const Slots = require("./Slots");
const MultiSlots = require("./MultiSlots");
const {isDevProblemPage} = require("../../../shared/PageShared");

// All of these atX functions are guaranteed to return a cursor into the
// given state. It may be a bad cursor which throws when you use a method
// like set, but you can always call the get or exists methods.

const atPage = function(state) {
	return CR(state).at("page");
};

const atUser = function(state) {
	return CR(state).at("user");
};

const atTutorial = function(state) {
	return CR(state).at("tutorial");
};

const atTutorialBlock = function(state) {
	return atMiscBlock(state, "tutorial");
};

const atTutorialResult = function(state) {
	return fromBlockToResult(atTutorialBlock(state));
};

const atTutorialCurrentProblemIndex = function(state) {
	const page = atPage(state).get();
	if (!_.isNil(page.problemIndex) && !_.isInteger(page.problemIndex)) {
		return atPage(state).in("problemIndex");
	}
	return atTutorialResult(state).in("currentProblemIndex");
};

const atTutorialCurrentStepName = function(state) {
	return atTutorial(state).in("currentStep");
};

const atTutorialCurrentPartName = function(state) {
	return atTutorial(state).in("currentPart");
};

const atTutorialVideo = function(state) {
	return atTutorial(state).in("video");
};

const atTutorialBook = function(state) {
	return atTutorial(state).in("book");
};

const atTutorialProblemsWorkedOn = function(state) {
	return atTutorial(state).in("problemsWorkedOn");
};

const atTutorialPartsVisited = function(state) {
	return atTutorial(state).in("partsVisited");
};

const atReadAloud = function(state) {
	return CR(state).at("readAloud");
};

// Used to get the correct user "smartly" without tampering with atUser
const atPageUser = function(state) {
	return state.page.demo ? atTempUser(state) : atUser(state);
};

const atTempUser = function(state) {
	return CR(state).at("tempUser");
};

const atCart = function(state) {
	return CR(state).at("cart");
};

const atDemo = function(state) {
	return CR(state).at("demo");
};

const atDemoData = function(state) {
	return CR(state).at(["demo", "data"]);
};

const atParentAccount = function(state) {
	return CR(state).at("parentAccount");
};

const atParentPayments = function(state) {
	return CR(state).at(["parentAccount", "payments"]);
};

const atTimeTracking = function(state) {
	return CR(state).at(["timeTracking"]);
};

const atMarketing = function(state) {
	return CR(state).at("marketing");
};

const atOfficeFlag = function(state, flagName) {
	return CR(state).at(["office", flagName]);
};

const atOfficeStudentData = function(state, studentID) {
	return CR(state).at(["officeStudentData", "students", studentID]);
};

const atParentStudents = function(state) {
	return CR(state).at(["officeStudentData", "students"]);
};

const atUserStudents = function(state, studentID) {
	const studentsPath = ["user", "model", "students"];
	const studentIndex = _.findIndex(
		CR(state)
			.at(studentsPath)
			.get(),
		s => s.id === studentID
	);
	const fullPath = studentsPath.concat([studentIndex]);
	return CR(state).at(fullPath);
};

const atBeastBuilder = function(state) {
	return CR(state).at(["beastbuilder"]);
};

const atSavedBeasts = function(state) {
	return atBeastBuilder(state).in(["savedBeasts"]);
};

const atBeastBuilderSaveModal = function(state) {
	return atBeastBuilder(state).in(["saveModal"]);
};

// This needs to change if we support multiple beasts later
const atBeast = function(state) {
	const path = ["beastbuilder", "beast"];
	const defaultValue = LoadState.loadInit({parts: []});
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atBeastPartsState = function(state) {
	return atBeast(state).in("partsState");
};

const atPartsInventory = function(state) {
	const path = ["beastbuilder", "inventory"];
	const defaultValue = LoadState.loadInit({parts: {}});
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atBeastPart = function(state, partID) {
	return atPartsInventory(state).in(["parts", partID]);
};

const atChapter = function(state, chapterID) {
	const userID = atUser(state).getIn("userID");
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "chapters", chapterID]
		: ["userData", userID, "chapters", chapterID];
	const defaultValue = LoadState.loadInit({
		chapterID: chapterID,
		model: null,
	});
	return CR(state)
		.at(path)
		.withDefault(chapterID ? defaultValue : undefined);
};

const atChapters = function(state) {
	const userID = atUser(state).getIn("userID");
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "chapters"]
		: ["userData", userID, "chapters"];
	return CR(state).at(path);
};

const atChapterByParams = function(state, gradeNumber, chapterIndex) {
	const chapterID = GradeChapter.toChapterID(gradeNumber, chapterIndex);
	if (!chapterID) {
		return CR(state).bad();
	}
	return atChapter(state, chapterID);
};

const atChapterByPage = function(state, page) {
	if (!page) {
		return CR(state).bad();
	}
	return atChapterByParams(state, page.gradeNumber, page.chapterIndex);
};

const atCurrChapter = function(state) {
	return atChapterByPage(state, atPage(state).get());
};

const atStudentChapter = function(state, studentID, chapterID) {
	return CR(state).at([
		"officeStudentData",
		"students",
		studentID,
		"byChapter",
		chapterID,
	]);
};

const atStudentGrade = function(state, studentID, gradeID) {
	return CR(state).at([
		"officeStudentData",
		"students",
		studentID,
		"byGrade",
		gradeID,
	]);
};

const atStudentGradeForClass = function(state, studentID, classID, gradeID) {
	return CR(state).at([
		"officeStudentData",
		classID,
		"students",
		studentID,
		"byGrade",
		gradeID,
	]);
};

const atClassChapter = function(state, classID, chapterID) {
	return CR(state).at([
		"officeStudentData",
		"classes",
		classID,
		"byChapter",
		chapterID,
	]);
};

const atStudentChapterForClass = function(
	state,
	studentID,
	classID,
	chapterID
) {
	return CR(state).at([
		"officeStudentData",
		classID,
		"students",
		studentID,
		"byChapter",
		chapterID,
	]);
};

const atClassBlock = function(state, classID, chapterID, blockID) {
	return CR(state).at([
		"contentReport",
		classID + "_" + chapterID,
		"blockData",
		blockID,
	]);
};

const atBlocks = function(state) {
	const userID = atUser(state).getIn("userID");
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo ? ["tempUser", "blocks"] : ["userData", userID, "blocks"];
	return CR(state).at(path);
};

const atBlockForUser = function(state, userID, blockID) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "blocks", blockID]
		: ["userData", userID, "blocks", blockID];
	return CR(state).at(path);
};

const atBlock = function(state, blockID) {
	const userID = atUser(state).getIn("userID");
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "blocks", blockID]
		: ["userData", userID, "blocks", blockID];
	return CR(state).at(path);
};

const atChapterByBlockID = function(state, blockID) {
	const chapterID = atBlock(state, blockID).getIn("model.chapterID");
	return atChapter(state, chapterID);
};

const atMiscBlock = function(state, miscBlockName) {
	const userID = atUser(state).getIn("userID");
	const path = ["userData", userID, "miscBlocks", miscBlockName];
	return CR(state).at(path);
};

const atBlockByResultSource = function(state, resultSource) {
	if (
		resultSource.miscBlockName ||
		_.get(resultSource, "blockType", null) === "misc"
	) {
		return atMiscBlock(state, resultSource.miscBlockName);
	} else if (resultSource.type === "lab") {
		return atLabBlock(state, resultSource.blockID);
	} else if (resultSource.blockID) {
		return atBlock(state, resultSource.blockID);
	} else if (resultSource.type === "dev") {
		return atDevProblem(state).in("blockInfo");
	} else {
		return CR(state).bad();
	}
};

const atBlockByParams = function(
	state,
	gradeNumber,
	chapterIndex,
	blockIndex,
	blockType,
	forTempUser,
	bookTestHash
) {
	let blockID;

	if (bookTestHash) {
		return atMiscBlock(state, bookTestHash);
	} else {
		const chapterCR = atChapterByParams(state, gradeNumber, chapterIndex);

		if (!chapterCR.exists()) {
			return CR(state).bad();
		} else if (blockType === "character") {
			blockID = chapterCR.getIn(["model", "blockIDs", blockIndex]);
		} else {
			blockID = chapterCR.getIn(["model", blockType + "BlockID"]);
		}
	}

	return blockID ? atBlock(state, blockID) : CR(state).bad();
};

const atBlockByPage = function(state, page) {
	if (!page) {
		return CR(state).bad();
	} else if (page.name === "tutorial.main") {
		return atTutorialBlock(state);
	}
	return atBlockByParams(
		state,
		page.gradeNumber,
		page.chapterIndex,
		page.blockIndex,
		page.blockType,
		page.demo,
		page.bookTestHash
	);
};

const atCurrBlock = function(state) {
	return atBlockByPage(state, atPage(state).get());
};

const atTheater = function(state) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo ? ["tempUser", "theater"] : ["theater"];
	return CR(state).at(path);
};

const atTheaterChapter = function(state, chapterID) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "theater", "chapters", chapterID]
		: ["theater", "chapters", chapterID];
	const defaultValue = LoadState.loadInit({});
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atTheaterSet = function(state, videoSetID) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "theater", "videoSets", videoSetID]
		: ["theater", "videoSets", videoSetID];
	const defaultValue = LoadState.loadInit({});
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atTheaterChapterByPage = function(state, page) {
	const chapterID = GradeChapter.toChapterID(
		page.gradeNumber,
		page.chapterIndex
	);
	if (!chapterID) {
		return CR(state).bad();
	}
	return atTheaterChapter(state, chapterID);
};

const atTheaterSetByPage = function(state, page) {
	const chapter = atTheaterChapterByPage(state, page).get();
	const chapterBlocks = _.get(chapter, "blocks", []);
	const block = _.find(chapterBlocks, b => b.blockID === page.blockID);
	const videoSetID = _.get(block, "videoSetID");
	return atTheaterSet(state, videoSetID);
};

const atLibraryChapter = function(state, chapterID) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const chapterContext = GradeChapter.chapterIDToContext(chapterID);
	if (!chapterContext) {
		return CR(state).bad();
	}
	const gradeNumber = chapterContext.gradeNumber;
	const path = demo
		? ["tempUser", "library", "chapters", gradeNumber, chapterID]
		: ["library", "chapters", gradeNumber, chapterID];
	const defaultValue = LoadState.loadInit({
		chapterID: chapterID,
		model: null,
	});
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atLibraryChaptersByGrade = function(state, gradeNumber) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "library", "chapters", gradeNumber]
		: ["library", "chapters", gradeNumber];
	const defaultValue = LoadState.loadInit({});
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atLibraryChapterByPage = function(state, page) {
	if (!page) {
		return CR(state).bad();
	}
	return atLibraryChapterByParams(state, page.gradeNumber, page.chapterIndex);
};

const atLibraryChapterByParams = function(state, gradeNumber, chapterIndex) {
	const chapterID = GradeChapter.toChapterID(gradeNumber, chapterIndex);
	if (!chapterID) {
		return CR(state).bad();
	}
	return atLibraryChapter(state, chapterID);
};

const atCurrLibraryChapter = function(state) {
	return atLibraryChapterByPage(state, atPage(state).get());
};

const atAllLibraryChapters = function(state) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo
		? ["tempUser", "library", "chapters"]
		: ["library", "chapters"];
	return CR(state).at(path);
};

// No parameters since only one pages object is remembered at a time.
const atLibraryPages = function(state) {
	const demo = CR(state)
		.at(["page", "demo"])
		.get();
	const path = demo ? ["tempUser", "library", "pages"] : ["library", "pages"];
	return CR(state).at(path);
};

const atLabType = function(state, typeID) {
	const userID = atUser(state).getIn("userID");
	const path = ["userData", userID, "labTypes", typeID];
	const defaultValue = LoadState.loadInit({
		typeID: typeID,
		model: null,
	});
	return CR(state)
		.at(path)
		.withDefault(typeID ? defaultValue : {});
};

const atLabTypes = function(state) {
	const userID = atUser(state).getIn("userID");
	const path = ["userData", userID, "labTypes"];
	return CR(state).at(path);
};

const atLabTypeByParams = function(state, typeID) {
	if (!typeID) {
		return CR(state).bad();
	}
	return atLabType(state, typeID);
};

const atLabTypeByPage = function(state, page) {
	if (!page) {
		return CR(state).bad();
	}
	return atLabTypeByParams(state, page.typeID);
};

const atLabTypeByBlockID = function(state, blockID) {
	const typeID = atLabBlock(state, blockID).getIn("model.typeID");
	return atLabType(state, typeID);
};

const atAllLabTypes = function(state, userID) {
	if (!userID) userID = atUser(state).getIn("userID");
	const path = ["userData", userID, "allLabTypes"];
	const defaultValue = LoadState.loadInit({});
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atLabBlock = function(state, blockID) {
	const user = atUser(state).get();
	const path = ["userData", user.userID, "labBlocks", blockID];
	return CR(state).at(path);
};

const atLabBlocks = function(state) {
	const user = atUser(state).get();
	const path = ["userData", user.userID, "labBlocks"];
	return CR(state).at(path);
};

const atLabBlockByPage = function(state, page) {
	if (!page) {
		return CR(state).bad();
	}
	return atLabBlockByParams(state, page.typeID, page.blockID);
};

const atLabBlockByParams = function(state, typeID, blockID) {
	const typeCR = atLabTypeByParams(state, typeID);
	if (!typeCR.exists()) {
		return CR(state).bad();
	}
	return blockID ? atLabBlock(state, blockID) : CR(state).bad();
};

const atCurrLabBlock = function(state) {
	return atLabBlockByPage(state, atPage(state).get());
};

// Internal helper only.
const fromBlockToResult = function(blockCR, trophy) {
	return blockCR.in(trophy ? ["trophy", "trophyResult"] : "currentResult");
};

const atResultBySource = function(state, source) {
	if (!source) {
		return CR(state).bad();
	} else if (source.type === "dev") {
		return CR(state).at(["devProblem", "result"]);
	} else if (source.type === "tutorial") {
		return atTutorialResult(state);
	} else if (
		source.type === "block" ||
		source.type === "trophy" ||
		source.type === "demoBlock" ||
		source.type === "demoTrophy"
	) {
		const blockCR = source.miscBlockName
			? atMiscBlock(state, source.miscBlockName)
			: atBlock(state, source.blockID);
		return fromBlockToResult(blockCR, source.type === "trophy");
	} else if (source.type === "lab") {
		return fromBlockToResult(atLabBlock(state, source.blockID));
	} else {
		const blockCR = atBlockByResultSource(state, source);
		const isTrophy = source.type === "trophy";
		return fromBlockToResult(blockCR, isTrophy);
	}
};

const atResultByPage = function(state, page) {
	if (!page) {
		return CR(state).bad();
	} else if (isDevProblemPage(page)) {
		return CR(state).at(["devProblem", "result"]);
	} else if (page.name === "tutorial.main") {
		return atTutorialResult(state);
	} else if (page.name === "trainer.problem") {
		return fromBlockToResult(
			atBlockByPage(state, page),
			page.problemIndex === "trophy"
		);
	} else if (
		page.name === "trainer.history" &&
		page.problemIndex !== "history"
	) {
		const blockCR = atBlockByPage(state, page);
		const resultID = blockCR.getIn(["history", "lastCompletedResultID"]);
		const allResults = blockCR.getIn(["history", "allResults"]);
		const ind = _.findIndex(allResults, r => r.resultID === resultID);
		return blockCR.in(["history", "allResults", ind]);
	} else if (page.name === "lab.problem") {
		return fromBlockToResult(atLabBlockByPage(state, page), false);
	}
	return CR(state).bad();
};

const atCurrResult = function(state) {
	return atResultByPage(state, atPage(state).get());
};

const atProblemByPageIndex = function(state, page, index) {
	if (!page) {
		return CR(state).bad();
	} else if (isDevProblemPage(page)) {
		return CR(state).at(["devProblem", "result", "problems", 0]);
	} else if (page.name === "tutorial.main") {
		return atTutorialResult(state).in([
			"problems",
			atTutorialCurrentProblemIndex(state).get(),
		]);
	} else if (page.name === "trainer.problem") {
		const resultCR = fromBlockToResult(
			atBlockByPage(state, page),
			index === "trophy"
		);
		return resultCR.in(["problems", index === "trophy" ? 0 : index]);
	} else if (page.name === "lab.problem") {
		const resultCR = fromBlockToResult(atLabBlockByPage(state, page), false);
		return resultCR.in(["problems", index]);
	} else if (page.name === "trainer.history") {
		const resultCR = atResultByPage(state, page);
		return resultCR.in(["problems", index]);
	}
	return CR(state).bad();
};

const atProblemByPage = function(state, page) {
	if (!page) {
		return CR(state).bad();
	}
	return atProblemByPageIndex(state, page, page.problemIndex);
};

// Assumes a problem in the current result is desired. index should be either a
// number or the string "trophy".
const atProblemByIndex = function(state, index) {
	const page = atPage(state).get();
	return atProblemByPageIndex(state, page, index);
};

const atCurrProblem = function(state) {
	const page = atPage(state).get();
	if (!page) {
		return CR(state).bad();
	}
	return atProblemByPageIndex(state, page, page.problemIndex);
};

const atDevProblem = function(state) {
	return CR(state).at("devProblem");
};

// School space

const atReportResult = function(
	state,
	{userID, resultID, blockID, isTrophy, space}
) {
	const path = Slots.table.reportResults({
		userID,
		resultID,
		blockID,
		isTrophy,
		space,
	}).keyList;
	return CR(state).at(path);
};

const atClasses = function(state, year) {
	const path = MultiSlots.table.reportClasses().keyList;
	return CR(state).at(path);
};

const atClass = function(state, classID) {
	let path = MultiSlots.table.reportClasses().keyList;
	path = path.concat([classID, "data"]);
	return CR(state).at(path);
};

const atClassBookTestBlock = function(state, classID, bookHash) {
	const testBlockInfoCR = atClass(state, classID).in(["testBlockInfo"]);
	const index = _.findIndex(
		testBlockInfoCR.get([]),
		r => r.miscBlockName === "bookTest" + bookHash
	);
	if (!testBlockInfoCR.exists() || index === -1) {
		return CR(state).bad();
	}
	return testBlockInfoCR.in(index);
};

const atLabLockSchedule = function(state) {
	return CR(state).at(["report", "labLockSchedule"]);
};

const atReportBlockNames = function(state, chapterID) {
	let path = MultiSlots.table.chapterBlocks().keyList;
	path = path.concat([chapterID, "data", "blocks"]);
	return CR(state).at(path);
};

const atReportAssignedBlocks = function(state, classID) {
	return CR(state).at(["report", "assignedBlocks", classID, "data"]);
};

const atUserAssignedBlocks = function(state) {
	return CR(state).at(["user", "model", "homework", "assignedBlocks"]);
};

const atReportBookTestBlocks = function(state) {
	const path = ["report", "bookTestBlocks"];
	return CR(state).at(path);
};

const atStudents = function(state) {
	const path = MultiSlots.table.reportStudents().keyList;
	return CR(state).at(path);
};

const atStudent = function(state, studentID) {
	const basePath = MultiSlots.table.reportStudents().keyList;
	const fullPath = basePath.concat([studentID, "data"]);
	return CR(state).at(fullPath);
};

const atSchool = function(state) {
	return CR(state).at("school");
};

const atSingleClass = function(state) {
	const path = Slots.table.singleClass().keyList;
	return CR(state).at(path);
};

const atSchoolStudentGrade = function(state, studentID, gradeID) {
	const basePath = MultiSlots.table.reportGradeChapter().keyList;
	const fullPath = basePath.concat([studentID, "data", "byGrade", gradeID]);
	return CR(state).at(fullPath);
};

const atSchoolStudentGradeChapter = function(state, studentID, chapterID) {
	const basePath = MultiSlots.table.reportGradeChapter().keyList;
	const fullPath = basePath.concat([studentID, "data", "byChapter", chapterID]);
	return CR(state).at(fullPath);
};

const atSchoolStudentChapterBlocks = function(state) {
	const basePath = ["school", "report", "chapterBlock"];
	return CR(state).at(basePath);
};

const atSchoolStudentBlock = function(state, studentID, blockID) {
	const blocksPath = ["school", "report", "blocksByChapter"];
	let chapBlocks;
	const chapterID = _.findKey(
		CR(state)
			.at(blocksPath)
			.get(),
		bls => {
			const blocks = bls.data.blocks;
			if (_.find(blocks, b => b.id === blockID)) {
				chapBlocks = blocks;
				return true;
			} else {
				return false;
			}
		}
	);
	const blockIndex = _.findIndex(chapBlocks, b => b.id === blockID);
	const basePath = MultiSlots.table.reportBlockResults(chapterID).keyList;
	const fullPath = basePath.concat([
		studentID,
		"data",
		"byBlockNumber",
		blockIndex + 1,
	]);
	return CR(state).at(fullPath);
};

const atSchoolStudentBlockChapter = function(state, studentID, chapterID) {
	const basePath = MultiSlots.table.reportBlockResults(chapterID).keyList;
	const fullPath = basePath.concat([studentID, "data", "chapterTotals"]);
	return CR(state).at(fullPath);
};

const atSchoolStudentBlockGrade = function(state, studentID, chapterID) {
	const basePath = MultiSlots.table.reportBlockResults(chapterID).keyList;
	const fullPath = basePath.concat([
		studentID,
		"data",
		"chapterTotals",
		"grade",
	]);
	return CR(state).at(fullPath);
};

const atAllLocks = function(state) {
	return CR(state).at(["lockOverrides"]);
};

const atUserOrClassLocks = function(state, obj) {
	if (obj.classID) {
		return atClassLocks(state, obj.classID);
	} else if (obj.userID) {
		return atUserLocks(state, obj.userID);
	}
	return CR(state).bad();
};

const atUserLocks = function(state, userID) {
	return CR(state).at(["lockOverrides", "user" + userID]);
};

const atClassLocks = function(state, classID) {
	return CR(state).at(["lockOverrides", "class" + classID]);
};

const atSchoolAccounts = function(state) {
	return CR(state).at(["school", "accounts"]);
};

const atSchoolRosters = function(state) {
	return CR(state).at(["school", "accounts", "rosters"]);
};

const atSchoolRostersValidation = function(state) {
	return atSchoolRosters(state).in("validation");
};

const atSchoolRostersGeneration = function(state) {
	return atSchoolRosters(state).in("generation");
};

const atSchoolRostersProcessing = function(state) {
	return atSchoolRosters(state).in("processing");
};

const atStaffRoster = function(state) {
	return atSchoolRosters(state).at(["model", "staffRoster"]);
};

const atStudentRoster = function(state) {
	return atSchoolRosters(state).at(["model", "studentRoster"]);
};

const atSchoolAccountStudent = function(state, userID) {
	const path = ["school", "accounts", "students", userID];
	return CR(state).at(path);
};

const atSchoolAccountStaff = function(state, userID) {
	const path = ["school", "accounts", "staff", userID];
	return CR(state).at(path);
};

const atSchoolAccountClass = function(state, classID) {
	const path = ["school", "accounts", "classes", classID];
	return CR(state).at(path);
};

const atSchoolAccountSchool = function(state, schoolID) {
	const path = ["school", "accounts", "schools", schoolID];
	return CR(state).at(path);
};

const atSchoolAccountPurchase = function(state) {
	const path = ["school", "accounts", "purchase"];
	return CR(state).at(path);
};

const atSchoolEntities = function(state) {
	return CR(state).at(["school", "entities", "relations"]);
};

const atCMSInfoPage = function(state, path) {
	const defaultValue = LoadState.loadInit({
		model: null,
	});
	return CR(state)
		.at(["cmsInfoPage", path.join("__")])
		.withDefault(path.length ? defaultValue : undefined);
};

const atLeaderboards = function(state) {
	return CR(state).at(["leaderboards"]);
};

const atLeaderboardsRankings = function(state, obj) {
	const defaultValue = LoadState.loadInit({});
	const {
		isAcademyUser,
		date,
		metric,
		usernameFilter,
		timeWindow,
		level,
		campus,
	} = obj;
	let path;
	if (isAcademyUser) {
		path = ["leaderboards", metric, level, campus];
	} else {
		path = ["leaderboards", date, metric, usernameFilter, timeWindow];
	}
	return CR(state)
		.at(path)
		.withDefault(defaultValue);
};

const atChapterPath = function(state) {
	const path = ["chapterPath"];
	return CR(state).at(path);
};

module.exports = {
	atPage,
	atUser,
	atTutorial,
	atTutorialBlock,
	atTutorialResult,
	atTutorialCurrentProblemIndex,
	atTutorialCurrentStepName,
	atTutorialCurrentPartName,
	atTutorialVideo,
	atTutorialBook,
	atTutorialProblemsWorkedOn,
	atTutorialPartsVisited,
	atReadAloud,
	atPageUser,
	atCart,
	atDemo,
	atDemoData,
	atTempUser,
	atParentAccount,
	atParentPayments,
	atTimeTracking,
	atMarketing,
	atOfficeFlag,
	atOfficeStudentData,
	atParentStudents,
	atUserStudents,
	atBeastBuilder,
	atSavedBeasts,
	atBeastBuilderSaveModal,
	atBeast,
	atBeastPartsState,
	atPartsInventory,
	atBeastPart,
	atClasses,
	atClass,
	atClassBookTestBlock,
	atStudents,
	atStudent,
	atReportBlockNames,
	atReportAssignedBlocks,
	atUserAssignedBlocks,
	atReportResult,
	atReportBookTestBlocks,
	atChapterPath,
	atChapter,
	atChapters,
	atChapterByParams,
	atChapterByPage,
	atCurrChapter,
	atStudentChapter,
	atStudentChapterForClass,
	atStudentGrade,
	atStudentGradeForClass,
	atClassChapter,
	atClassBlock,
	atBlocks,
	atBlock,
	atBlockForUser,
	atChapterByBlockID,
	atMiscBlock,
	atBlockByResultSource,
	atBlockByParams,
	atBlockByPage,
	atCurrBlock,
	atResultBySource,
	atResultByPage,
	atCurrResult,
	atProblemByPageIndex,
	atProblemByPage,
	atProblemByIndex,
	atCurrProblem,
	atDevProblem,
	atLibraryChapter,
	atLibraryChaptersByGrade,
	atLibraryChapterByParams,
	atLibraryChapterByPage,
	atCurrLibraryChapter,
	atAllLibraryChapters,
	atLibraryPages,
	atLabType,
	atLabTypes,
	atLabTypeByParams,
	atLabTypeByPage,
	atLabTypeByBlockID,
	atAllLabTypes,
	atLabBlocks,
	atLabBlock,
	atLabBlockByParams,
	atLabBlockByPage,
	atLabLockSchedule,
	atCurrLabBlock,
	atTheater,
	atTheaterChapter,
	atTheaterSet,
	atTheaterChapterByPage,
	atTheaterSetByPage,
	atSchool,
	atSingleClass,
	atSchoolStudentGrade,
	atSchoolStudentGradeChapter,
	atSchoolStudentBlock,
	atSchoolStudentBlockGrade,
	atSchoolStudentBlockChapter,
	atSchoolStudentChapterBlocks,
	atAllLocks,
	atUserOrClassLocks,
	atUserLocks,
	atClassLocks,
	atSchoolAccounts,
	atSchoolRosters,
	atSchoolRostersValidation,
	atSchoolRostersGeneration,
	atSchoolRostersProcessing,
	atStaffRoster,
	atStudentRoster,
	atSchoolAccountStudent,
	atSchoolAccountStaff,
	atSchoolAccountClass,
	atSchoolAccountSchool,
	atSchoolAccountPurchase,
	atSchoolEntities,
	atCMSInfoPage,
	atLeaderboards,
	atLeaderboardsRankings,
};
